import { ref, computed, type Ref, type ComputedRef } from 'vue'
import type { ZodSchema } from 'zod'
import { diffObjects, serializeForValidation } from '@/utils/sanitizer'

/**
 * Options for configuring form validation behavior
 */
export interface UseFormValidationOptions<T extends Record<string, unknown>> {
    /**
     * Optional reference to original values for diff-based validation.
     * When provided, only changed fields are validated (useful for update operations)
     */
    originalValues?: Ref<T | undefined>

    /**
     * Whether to serialize Date objects to ISO strings before validation.
     * Enable this when your API expects string dates but your form uses Date objects.
     */
    serializeBeforeValidation?: boolean
}

/**
 * Field helper interface returned by createFieldHelper
 */
interface FieldHelper {
    invalid: ComputedRef<boolean>
    error: ComputedRef<string>
    hasError: ComputedRef<boolean>
    onUpdate: () => void
}

/**
 * Return type of useFormValidation composable
 */
interface UseFormValidationReturn {
    errors: Ref<Record<string, string>>
    isValid: ComputedRef<boolean>
    validate: () => boolean
    createFieldHelper: (fieldName: string) => FieldHelper
    reset: () => void
}

/**
 * Provides validation functionality for section-based forms.
 * Validates form data against a Zod schema and provides field-level error helpers.
 *
 * @param modelValue - Reactive reference to the form data
 * @param schema - Zod schema for validating a section of the form (Partial<T>)
 * @param translateFn - Translation function for error messages (e.g., from i18next)
 * @param options - Optional configuration for validation behavior
 * @returns Validation state and helpers for form fields
 */
export function useFormValidation<T extends Record<string, unknown>>(
    modelValue: Ref<T>,
    schema: ZodSchema<Partial<T>>,
    translateFn: (key: string) => string,
    options: UseFormValidationOptions<T> = {}
): UseFormValidationReturn {
    const { originalValues, serializeBeforeValidation = false } = options

    const errors = ref<Record<string, string>>({})
    const hasValidated = ref(false)

    /**
     * Validates the current form state synchronously.
     * Should be called before saving or navigating to ensure data validity.
     */
    const validate = (): boolean => {
        hasValidated.value = true

        // Merge strategy: Start with full current value, override with touched changes
        // This ensures required fields are always included even if not touched
        const touched = originalValues?.value
            ? diffObjects(originalValues.value, modelValue.value)
            : modelValue.value

        const toValidate = originalValues?.value
            ? { ...modelValue.value, ...touched }
            : modelValue.value

        // Optionally serialize Date objects to strings for validation (API compatibility)
        const data = serializeBeforeValidation ? serializeForValidation(toValidate) : toValidate

        const result = schema.safeParse(data)

        if (result.success) {
            errors.value = {}
            return true
        }

        const newErrors: Record<string, string> = {}
        result.error.issues.forEach((issue) => {
            const fieldName = String(issue.path[0] || '')
            if (fieldName) {
                // Translate the Zod error message directly
                // Schemas should provide complete i18n keys (e.g., 'ui.events.wizard.validation.title')
                newErrors[fieldName] = translateFn(issue.message)
            }
        })

        errors.value = newErrors
        return false
    }

    /**
     * Creates reactive helpers for binding validation state to form fields.
     * Use these helpers to show validation errors and invalid states in the UI.
     *
     * @param fieldName - The name of the field to create helpers for
     */
    const createFieldHelper = (fieldName: string) => ({
        invalid: computed(() => hasValidated.value && !!errors.value[fieldName]),
        error: computed(() => errors.value[fieldName] || ''),
        hasError: computed(() => !!errors.value[fieldName]),

        /**
         * Call this when the field value changes to clear its error.
         * Bind to @update:model-value event.
         */
        onUpdate: () => {
            if (errors.value[fieldName]) {
                const { [fieldName]: _, ...rest } = errors.value
                errors.value = rest
            }
        },
    })

    const reset = () => {
        errors.value = {}
        hasValidated.value = false
    }

    return {
        errors,
        isValid: computed(() => Object.keys(errors.value).length === 0),
        validate,
        createFieldHelper,
        reset,
    }
}
